import {
  Box3,
  BufferGeometry,
  Line3,
  Matrix4,
  Object3D,
  Plane,
  Quaternion,
  Sphere,
  Vector3,
} from 'three';
import { MeshBVH, StaticGeometryGenerator } from 'three-mesh-bvh';

import { closestPointInNavMesh, isPointInNavMesh } from '@spazious/dollhouse-navmesh';
import { Outline2D, World } from '@spazious/dollhouse-three';

import { ColliderNode, FNode, FNodeTypesEnum } from '../types/index.js';
import { getStackingParentSelector } from '../utils/index.js';

const weakMap = new Map<string, BufferGeometry>();

const invMat = new Matrix4();
const _sphere = new Sphere();
const _position = new Vector3();
const _va = new Vector3();
const _vb = new Vector3();

const _line = new Line3();
/**
 * Get the intersection points between a plane and a MeshBVH collider.
 * @param {MeshBVH} collider The MeshBVH collider.
 * @param {Plane} plane The plane to intersect with.
 * @returns {Vector3[]} An array of intersection points.
 */
const planeIntersections = (collider: MeshBVH, plane: Plane): Vector3[] => {
  const result: Vector3[] = [];
  const resultSet = new Set<string>(); // track unique intersection points

  collider.shapecast({
    intersectsBounds: (box) => box.intersectsPlane(plane),
    intersectsTriangle(triangle) {
      // check each triangle edge for intersection with the plane
      // and add intersecting poins to result
      const { a, b, c } = triangle;

      const points: Vector3[] = [];
      _line.start.copy(a);
      _line.end.copy(b);
      if (plane.intersectLine(_line, _va)) {
        points.push(_va.clone());
      }

      _line.start.copy(b);
      _line.end.copy(c);
      if (plane.intersectLine(_line, _va)) {
        points.push(_va.clone());
      }

      _line.start.copy(c);
      _line.end.copy(a);
      if (plane.intersectLine(_line, _va)) {
        points.push(_va.clone());
      }

      // when the plane passes through a vertex and one of the edges of the triangle,
      // there will be three intersections, two of which must be repeated
      if (points.length === 3) {
        if (points[2].equals(points[1]) || points[2].equals(points[0])) {
          // 3rd point is a duplicate, ignore it
          points.pop();
        } else if (points[1].equals(points[0])) {
          // first two points are duplicate, use 1st adn 3rd points
          points[1].copy(points[2]);
          points.pop();
        }
      }

      if (points.length === 2) {
        const [p0, p1] = points;

        let key = `${p0.x},${p0.z}`;
        if (!resultSet.has(key)) {
          resultSet.add(key);
          result.push(p0);
        }

        key = `${p1.x},${p1.z}`;
        if (!resultSet.has(key)) {
          resultSet.add(key);
          result.push(p1);
        }
      }
    },
  });

  return result;
};

const getWorldPositionAndRotation = (
  node: ColliderNode,
  position: Vector3
): { position: Vector3; rotation: number } => {
  let rotation = node.rotation;
  position.set(node.position[0], 0, node.position[1]);

  let target: FNode | null = node.parent as any;
  for (; target?.position; ) {
    // rotate node position around parent position by parent rotation angle
    const c = Math.cos(target.rotation);
    const s = Math.sin(target.rotation);

    position.z *= -1; // projecting 3D to 2D point => project +ve Z-axis to -ve Y-axis

    const x = position.x * c - position.z * s;
    const y = position.x * s + position.z * c;

    position.x = x + target.position[0];
    position.z = y + target.position[1] * -1;
    position.z *= -1; // projecting 2D to 3D point => project -ve Y-axis tp +ve Z-axis

    rotation += target.rotation;
    // TODO all nodes should have a position and rotation
    target = (target.parent as FNode)?.position ? (target.parent as FNode | null) : null;
  }

  return { position, rotation };
};

const getMatrixWorld = (node: ColliderNode, positionTarget: Vector3) => {
  const { position, rotation } = getWorldPositionAndRotation(node, positionTarget);

  return new Matrix4().compose(
    position,
    new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), rotation),
    new Vector3(1, 1, 1)
  );
};

export class Collider {
  bvh: MeshBVH | null = null;
  box: Box3 = new Box3();
  node: ColliderNode;
  radius: number = 0;
  baseOutline: Outline2D | null = null;
  get geometry() {
    return weakMap.get(this.node.url) ?? null;
  }
  constructor(node: ColliderNode) {
    this.node = node;
  }

  generate(object: Object3D) {
    if (!weakMap.has(this.node.url)) {
      object.updateMatrixWorld(true);
      const geometry = new BufferGeometry();
      const generator = new StaticGeometryGenerator(object);
      generator.applyWorldTransforms = true;
      generator.generate(geometry);
      const bvh = new MeshBVH(geometry, {
        setBoundingBox: false,
      });
      geometry.boundsTree = bvh;

      const bbox = new Box3();
      bvh.getBoundingBox(bbox);
      // check intersection of the collider with a plane just above its bottom most point
      const basePoints = planeIntersections(
        bvh,
        new Plane(new Vector3(0, 1, 0), -bbox.min.y - 0.001)
      );
      geometry.baseOutline = new Outline2D().setFromPoints(basePoints.map((p) => [p.x, p.z]));

      weakMap.set(this.node.url, geometry);
    }

    this.bvh = this.geometry?.boundsTree ?? null;
    this.bvh?.getBoundingBox(this.box);
    this.box.getBoundingSphere(_sphere);
    this.radius = _sphere.radius;
    this.baseOutline = this.geometry?.baseOutline ?? null;
  }

  intersects(node: ColliderNode) {
    if (!this.geometry?.boundsTree || !node.collider?.geometry) {
      return { intersecting: false, positionY: null };
    }

    invMat.copy(getMatrixWorld(this.node, _va)).invert().multiply(getMatrixWorld(node, _vb));

    let intersecting = this.geometry?.boundsTree?.intersectsGeometry(
      node.collider.geometry,
      invMat
    );
    let positionY = null;
    if (intersecting && node.object?.navMesh) {
      _va.sub(_vb);

      const navMeshResult = closestPointInNavMesh(_va, node.object.navMesh.navMeshQuery, {
        distance: _vb.set(0.1, 3, 0.1),
      });
      if (navMeshResult.success && navMeshResult.isPointOverPoly) {
        positionY = navMeshResult.point.y;
      }
    }

    return { intersecting, positionY };
  }

  getAncestorOfType(
    node: FNode,
    nodeTypes: FNodeTypesEnum[] = [FNodeTypesEnum.FComposition]
  ): FNode | null {
    let parent: FNode | null = node.parent as FNode;
    while (parent) {
      if (nodeTypes.includes(parent.type)) {
        return parent;
      }
      parent = parent.parent as FNode;
    }
    return null;
  }

  nodesWithSameAncestor(
    node1: ColliderNode,
    node2: ColliderNode,
    filter: FNodeTypesEnum[] = [FNodeTypesEnum.FComposition]
  ) {
    const parent1 = this.getAncestorOfType(node1, filter);
    const parent2 = this.getAncestorOfType(node2, filter);
    return parent1 && parent1 === parent2;
  }

  isNodeWithinNavmesh(node: ColliderNode, node2: ColliderNode) {
    const navMeshQuery = node2.object?.navMesh?.navMeshQuery;
    if (!navMeshQuery) {
      return true;
    }

    const baseOutline = node.collider?.baseOutline;
    if (!baseOutline) {
      console.warn('Base outline not set for node', node);
      return true;
    }

    // get base outline in local space of node2
    invMat.copy(getMatrixWorld(node2, _va)).invert().multiply(getMatrixWorld(node, _vb));
    const outline = baseOutline.array.map((p) => {
      _va.set(p[0], 0, p[1]);
      _va.applyMatrix4(invMat);
      return [_va.x, _va.z];
    });

    return outline.every((p) => {
      return isPointInNavMesh(_va.set(p[0], node.positionY ?? 0, p[1]), navMeshQuery);
    });
  }

  getCollisions(
    world: World,
    first: boolean = true
  ): { collisions: ColliderNode[]; positionY: number | null } | null {
    if (!world?.octree) {
      console.warn('octree is missing', this.node);
      return null;
    }

    const collisions: ColliderNode[] = [];
    let positionY = null;

    const objects = world.octree.retrieve(
      getWorldPositionAndRotation(this.node, _position).position,
      this.radius
    );

    for (const object of objects) {
      const node = object.userData.node as ColliderNode;
      if (!node) {
        console.warn('Missing node on the object', object, object.userData);
        continue;
      }

      if (
        (node.type === FNodeTypesEnum.Furniture && !node.visible) ||
        node.selector === this.node.selector ||
        // Do not check collisions for
        // the same parent to avoid collisions between siblings in the same composition
        (node.parent !== node.root && this.nodesWithSameAncestor(node, this.node))
      ) {
        continue;
      }

      const currentNodeParent = getStackingParentSelector(this.node);
      const otherNodeParent = getStackingParentSelector(node);
      if (otherNodeParent && otherNodeParent === this.node.selector) {
        // node is stacked on this.node
        continue;
      } else if (currentNodeParent && currentNodeParent === node.selector) {
        // this.node is stacked on node
        // check if this.node is fully within node.navmesh
        if (this.node.type === FNodeTypesEnum.Furniture) {
          if (!this.isNodeWithinNavmesh(this.node, node)) {
            collisions.push(node);
          }
        }
      } else {
        // check intersections
        const result = this.intersects(node);
        if (!this.node.shallowParent) {
          positionY = result.positionY;
        }
        if (result.intersecting) {
          collisions.push(node);
        }
      }

      if (first && collisions.length > 0) {
        break; // stop traversing here
      }
    }

    return { collisions, positionY };
  }
}
